import { useState, useEffect } from "react";
import {
  Printer,
  Search,
  Table as TableIcon,
  LayoutGrid,
  FileSpreadsheet,
  FileText,
  Loader2,
} from "lucide-react";
import api from "../api/axiosInstance";
import AdvanceAndFinalize from "./AdvanceAndFinalize";

const MonthlyPayoutSummary = () => {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("table"); // 'table' or 'card'
  const [activeSingleDownload, setActiveSingleDownload] = useState(null);
  const [downloadingBulk, setDownloadingBulk] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7),
  );

  useEffect(() => {
    loadPayouts();
  }, [selectedMonth]);

  const loadPayouts = async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `get_monthly_payouts.php?month=${selectedMonth}`,
      );
      setPayouts(res.data.payouts || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredPayouts = payouts.filter((p) =>
    p.employee_name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const downloadSinglePDF = async (empId, name) => {
    setActiveSingleDownload(empId);
    try {
      const response = await api.get(
        `/generate_payouts_pdf.php?month=${selectedMonth}&emp_id=${empId}`,
        {
          responseType: "blob",
        },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Salary_Slip_${name}_${selectedMonth}.pdf`);
      link.click();
    } catch (err) {
      alert("PDF Error");
    } finally {
      setActiveSingleDownload(null);
    }
  };

  const downloadBulkSlips = async () => {
    setDownloadingBulk(true);
    try {
      const response = await api.get(
        `/generate_payouts_pdf.php?month=${selectedMonth}`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `ClientOrbit_Bulk_Payouts_${selectedMonth}.pdf`);
      link.click();
    } catch (err) {
      alert("Bulk PDF Error");
    } finally {
      setDownloadingBulk(false);
    }
  };

  const exportToExcel = () => {
    setDownloadingExcel(true);
    // Small timeout to show the spinner before the heavy browser UI lock of CSV generation
    setTimeout(() => {
      try {
        const headers = [
          "Employee",
          "Type",
          "Days/Hrs",
          "Earnings",
          "Additions",
          "Deductions",
          "Advances",
          "Net Payable",
        ];
        const rows = payouts
          .filter((p) =>
            p.employee_name.toLowerCase().includes(searchTerm.toLowerCase()),
          )
          .map((p) => [
            p.employee_name,
            p.pay_type,
            p.days_present,
            p.base_earned,
            p.total_additions,
            p.total_deductions,
            p.total_advances,
            p.net_payable,
          ]);

        let csvContent =
          "data:text/csv;charset=utf-8," +
          headers.join(",") +
          "\n" +
          rows.map((e) => e.join(",")).join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `Payout_Report_${selectedMonth}.csv`);
        link.click();
      } catch (err) {
        alert("Excel Export Error");
      } finally {
        setDownloadingExcel(false);
      }
    }, 500);
  };

  return (
    <div className="space-y-8 p-4 animate-in fade-in duration-500">
      {/* Header Section */}
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-end gap-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic uppercase leading-none">
            Payout Ledger
          </h1>
          <p className="text-slate-500 font-medium italic mt-2">
            Settlement summary for {selectedMonth}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto">
          <div className="relative flex-1 md:flex-none">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search employee..."
              className="pl-12 pr-4 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-xs w-full md:w-64 outline-none focus:border-indigo-500 transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <input
            type="month"
            className="p-4 bg-white border border-slate-100 rounded-2xl font-black text-xs uppercase outline-none"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />

          <div className="bg-slate-100 p-1 rounded-2xl flex gap-1 shadow-inner">
            <button
              onClick={() => setViewMode("table")}
              className={`p-3 rounded-xl transition-all ${viewMode === "table" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-400"}`}
            >
              <TableIcon size={18} />
            </button>
            <button
              onClick={() => setViewMode("card")}
              className={`p-3 rounded-xl transition-all ${viewMode === "card" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-400"}`}
            >
              <LayoutGrid size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Quick Actions Bar */}
      <div className="flex flex-wrap gap-4">
        <button
          disabled={downloadingBulk}
          onClick={downloadBulkSlips}
          className="flex-1 md:flex-none bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase shadow-lg flex items-center justify-center gap-2 hover:bg-slate-900 transition-all disabled:opacity-50"
        >
          {downloadingBulk ? (
            <Loader2 className="animate-spin" size={18} />
          ) : (
            <FileText size={18} />
          )}
          {downloadingBulk ? "Generating Slips..." : "Bulk Slips (PDF)"}
        </button>
        <button
          disabled={downloadingExcel}
          onClick={exportToExcel}
          className="flex-1 md:flex-none bg-emerald-600 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase shadow-lg flex items-center justify-center gap-2 hover:bg-slate-900 transition-all disabled:opacity-50"
        >
          {downloadingExcel ? (
            <Loader2 className="animate-spin" size={18} />
          ) : (
            <FileSpreadsheet size={18} />
          )}
          {downloadingExcel ? "Preparing Sheet..." : "Export Sheet (Excel)"}
        </button>
      </div>

      <AdvanceAndFinalize
        selectedMonth={selectedMonth}
        payouts={payouts}
        onRefresh={loadPayouts}
      />

      {loading ? (
        <div className="p-20 text-center font-black animate-pulse text-slate-300 uppercase tracking-widest text-xl">
          Loading Ledger Data...
        </div>
      ) : (
        <>
          {viewMode === "table" ? (
            /* TABLE VIEW: Professional & Compact */
            <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
              <table className="w-full text-left border-collapse">
                <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-400 border-b">
                  <tr>
                    <th className="p-6">Employee</th>
                    <th className="p-6">Attendance</th>
                    <th className="p-6">Total Gross</th>
                    <th className="p-6">Advances</th>
                    <th className="p-6">Deductions</th>
                    <th className="p-6">Net Payable</th>
                    <th className="p-6 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filteredPayouts.map((p) => (
                    <tr
                      key={p.employee_id}
                      className="hover:bg-slate-50/50 transition-all group"
                    >
                      <td className="p-6">
                        <p className="font-black text-slate-900 uppercase italic leading-none">
                          {p.employee_name}
                        </p>
                        <p className="text-[9px] font-bold text-slate-400 uppercase mt-1">
                          {p.pay_type} Basis
                        </p>
                      </td>
                      <td className="p-6 font-bold text-slate-700 text-xs">
                        {p.days_present}
                      </td>
                      <td className="p-6 font-black text-slate-900 text-sm italic">
                        ₹{p.total_earned.toLocaleString()}
                      </td>
                      <td className="p-6 font-bold text-rose-400 text-xs">
                        -₹{p.total_advances.toLocaleString()}
                      </td>
                      <td className="p-6 font-bold text-slate-400 text-xs">
                        ₹{p.total_deductions.toLocaleString()}
                      </td>
                      <td
                        className={`p-6 font-black text-lg ${p.net_payable < 0 ? "text-rose-600" : "text-emerald-600"}`}
                      >
                        ₹{Math.abs(p.net_payable).toLocaleString()}
                      </td>
                      <td className="p-6 text-right">
                        <button
                          disabled={activeSingleDownload === p.employee_id}
                          onClick={() =>
                            downloadSinglePDF(p.employee_id, p.employee_name)
                          }
                          className="p-3 bg-slate-50 text-slate-400 rounded-xl hover:bg-indigo-600 hover:text-white transition-all disabled:bg-slate-100"
                        >
                          {activeSingleDownload === p.employee_id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Printer size={16} />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /* CARD VIEW: Existing detailed view */
            <div className="grid grid-cols-1 gap-4">
              {filteredPayouts.map((p) => {
                const isOverpaid = p.net_payable < 0;
                return (
                  <div
                    key={p.employee_id}
                    className="bg-white p-8 rounded-[40px] border border-slate-100 shadow-sm hover:shadow-xl transition-all"
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-center">
                      <div>
                        <h3 className="text-xl font-black text-slate-900 uppercase italic">
                          {p.employee_name}
                        </h3>
                        <span className="text-[10px] font-black text-indigo-600 uppercase bg-indigo-50 px-2 py-1 rounded">
                          {p.pay_type} Basis
                        </span>
                      </div>
                      <div className="lg:col-span-2 grid grid-cols-3 gap-4 border-x border-slate-50 px-8">
                        <div>
                          <p className="text-[8px] font-black text-slate-400 uppercase mb-1">
                            Total Earned
                          </p>
                          <p className="font-black text-slate-900">
                            ₹{p.total_earned.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-[8px] font-black text-slate-400 uppercase mb-1">
                            Deductions
                          </p>
                          <p className="font-black text-rose-500">
                            ₹{p.total_deductions.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-[8px] font-black text-slate-400 uppercase mb-1">
                            Attendance
                          </p>
                          <p className="font-black text-slate-900">
                            {p.days_present}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] font-black text-slate-400 uppercase mb-1">
                          {isOverpaid ? "Carry Forward" : "Net Payable Now"}
                        </p>
                        <p
                          className={`text-2xl font-black italic tracking-tighter ${isOverpaid ? "text-rose-600" : "text-emerald-600"}`}
                        >
                          ₹{Math.abs(p.net_payable).toLocaleString()}
                        </p>
                        <button
                          onClick={() =>
                            downloadSinglePDF(p.employee_id, p.employee_name)
                          }
                          className="mt-2 flex ml-auto items-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-xl font-black text-[10px] uppercase hover:bg-indigo-600 transition-all shadow-md"
                        >
                          <Printer size={14} /> Print Slip
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MonthlyPayoutSummary;
